import { zodResolver } from '@hookform/resolvers/zod';
import { Clock } from '@phosphor-icons/react';
import {
  getGetBrandProfileQueryKey,
  useGetBrandProfile,
  useUpdateBrandProfile,
} from '@reelplicate/api-client/brand-profiles';
import type { BrandProfileDto } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Button,
  Field,
  TagInput,
  TextArea,
  TextInput,
  toast,
  Well,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { siteHost } from '@/features/onboarding/api-values';

import { CaptionStylesSection } from './caption-styles-section';
import { SettingsCard, SettingsPageLayout, SettingsSection } from './settings-layout';

const BrandProfileSchema = z.object({
  product: z.string().trim().max(200),
  audience: z.string().trim().max(200),
  offer: z.string().trim().max(200),
  tone: z.array(z.string()).max(5, 'Pick up to 5 words'),
  approvedClaims: z.array(z.string()).max(12),
  prohibited: z.array(z.string()).max(12),
});
type BrandProfileValues = z.infer<typeof BrandProfileSchema>;

const EMPTY: BrandProfileValues = {
  product: '',
  audience: '',
  offer: '',
  tone: [],
  approvedClaims: [],
  prohibited: [],
};

const toValues = (p: BrandProfileDto): BrandProfileValues => ({
  product: p.product ?? '',
  audience: p.audience ?? '',
  offer: p.offer ?? '',
  tone: p.tone,
  approvedClaims: p.approvedClaims,
  prohibited: p.prohibited,
});

const dateFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

/**
 * Figma "Settings — Brand profile" (321:368): the brand profile the
 * onboarding brand step filled, now editable in full - basics, voice and
 * guardrails. Loads and saves through the brand-profile API. Figma's
 * version history (v4 → "Save as v5") isn't in the API yet, so the button
 * saves in place.
 */
export function BrandProfilePage() {
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const { data: profile } = useGetBrandProfile(workspace?.id ?? '', {
    query: { enabled: Boolean(workspace) },
  });
  const save = useUpdateBrandProfile();
  const queryClient = useQueryClient();
  const form = useForm<BrandProfileValues>({
    resolver: zodResolver(BrandProfileSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (profile && !form.formState.isDirty) form.reset(toValues(profile));
  }, [profile, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!workspace) return;
    try {
      await save.mutateAsync({
        id: workspace.id,
        data: { ...values, offer: values.offer || null },
      });
    } catch {
      return; // The failure is already shown as a toast.
    }
    form.reset(values);
    await queryClient.invalidateQueries({
      queryKey: getGetBrandProfileQueryKey(workspace.id),
    });
    toast.success('Brand profile saved');
  });

  const source = siteHost(profile?.sourceUrl);
  const edited = profile?.editedAt ?? profile?.readAt;

  return (
    <SettingsPageLayout
      title="Brand profile"
      description={`Everything Reelplicate knows about ${workspace?.name ?? 'your brand'}. Every draft, hook and caption is written from this.`}
      onSubmit={onSubmit}
      actions={
        <>
          <Button type="button" variant="ghost" leadingIcon={Clock}>
            Version history
          </Button>
          <Button
            type="submit"
            disabled={!form.formState.isDirty}
            loading={save.isPending}
          >
            Save changes
          </Button>
        </>
      }
    >
      {source && (
        <Well tone="brand" title={`Prefilled from ${source}`}>
          {edited
            ? `Last ${profile?.editedAt ? 'edited' : 'read'} ${dateFormat.format(new Date(edited))}. `
            : ''}
          Drafts are written from what’s saved here.
        </Well>
      )}

      <SettingsSection title="The basics" description="What you sell and who it is for.">
        <SettingsCard>
          <div className="flex flex-col gap-5 pt-5 pb-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <Controller
                name="product"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Brand or product"
                    error={fieldState.error?.message}
                    className="flex-1"
                  >
                    <TextInput
                      {...field}
                      placeholder="e.g. Oat & Ember — barista oat milk"
                    />
                  </Field>
                )}
              />
              <Controller
                name="audience"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Audience"
                    error={fieldState.error?.message}
                    className="flex-1"
                  >
                    <TextInput
                      {...field}
                      placeholder="e.g. Busy parents who love coffee"
                    />
                  </Field>
                )}
              />
            </div>
            <Controller
              name="offer"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field
                  label="Offer"
                  helper="The one thing every reel should make people want."
                  error={fieldState.error?.message}
                >
                  <TextArea
                    {...field}
                    placeholder="e.g. Oat milk that foams like dairy. 20% off your first case with code FOAM20."
                  />
                </Field>
              )}
            />
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Voice"
        description="How the brand sounds on camera and in captions."
      >
        <SettingsCard>
          <div className="pt-5 pb-6">
            <Controller
              name="tone"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field
                  label="Tone"
                  helper="Pick up to 5 words. Generated scripts are checked against them."
                  error={fieldState.error?.message}
                >
                  <TagInput
                    value={field.value}
                    onValueChange={field.onChange}
                    onBlur={field.onBlur}
                    max={5}
                    placeholder="Add a word"
                  />
                </Field>
              )}
            />
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Guardrails"
        description="Flows block any draft that breaks these, before a person ever sees it."
      >
        <SettingsCard>
          <div className="flex flex-col gap-5 pt-5 pb-6">
            <Controller
              name="approvedClaims"
              control={form.control}
              render={({ field }) => (
                <Field
                  label="Approved claims"
                  helper="Only these claims may appear in scripts and captions."
                >
                  <TagInput
                    value={field.value}
                    onValueChange={field.onChange}
                    onBlur={field.onBlur}
                    max={12}
                    placeholder="Add a claim"
                  />
                </Field>
              )}
            />
            <Controller
              name="prohibited"
              control={form.control}
              render={({ field }) => (
                <Field
                  label="Prohibited language"
                  helper="Words and phrases a draft must never use."
                >
                  <TagInput
                    value={field.value}
                    onValueChange={field.onChange}
                    onBlur={field.onBlur}
                    max={12}
                    placeholder="Add a phrase"
                  />
                </Field>
              )}
            />
          </div>
        </SettingsCard>
      </SettingsSection>

      {workspace && <CaptionStylesSection workspaceId={workspace.id} />}
    </SettingsPageLayout>
  );
}
